import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CollapseButtonProps {
  /** Whether the sidebar is currently collapsed */
  isCollapsed: boolean;
  /** Callback when the button is clicked to toggle collapse state */
  onToggle: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * CollapseButton - Atom component
 *
 * Toggle button for sidebar collapse state. Shows ChevronRight when collapsed
 * (clicking expands), ChevronLeft when expanded (clicking collapses).
 * Uses shadcn/ui Button with ghost variant for minimal visual footprint.
 *
 * Part of the atomic design system for NavigationSidebar.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CollapseButton isCollapsed={false} onToggle={toggleSidebar} />
 *
 * // When collapsed
 * <CollapseButton isCollapsed={true} onToggle={toggleSidebar} />
 *
 * // With custom className
 * <CollapseButton
 *   isCollapsed={isCollapsed}
 *   onToggle={toggleSidebar}
 *   className="absolute right-0"
 * />
 * ```
 */
export function CollapseButton({ isCollapsed, onToggle, className }: CollapseButtonProps) {
  const Icon = isCollapsed ? ChevronRight : ChevronLeft;
  const ariaLabel = isCollapsed ? "Expand sidebar" : "Collapse sidebar";

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onToggle}
      aria-label={ariaLabel}
      className={cn("h-8 w-8", className)}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}
